import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusPill from "../components/StatusPill";
import { useAuth } from "../contexts/AuthContext";

export default function ShipmentsPage() {
  const { user } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pallets, setPallets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    customer_id: "",
    ship_date: new Date().toISOString().slice(0, 10),
    truck_number: "",
    notes: "",
    pallet_ids: [],
  });

  const fetchAll = async () => {
    const [s, c] = await Promise.all([api.get("/shipments"), api.get("/customers")]);
    setShipments(s.data);
    setCustomers(c.data);
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (!form.customer_id) {
      setPallets([]);
      return;
    }
    api.get("/pallets", { params: { customer_id: form.customer_id, status: "in_stock" } }).then((r) => {
      // Also allow expiring_soon to be shipped
      api.get("/pallets", { params: { customer_id: form.customer_id, status: "expiring_soon" } }).then((r2) => {
        setPallets([...r.data, ...r2.data]);
      });
    });
  }, [form.customer_id]);

  const togglePallet = (id) => {
    setForm((f) => ({
      ...f,
      pallet_ids: f.pallet_ids.includes(id) ? f.pallet_ids.filter((x) => x !== id) : [...f.pallet_ids, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id || form.pallet_ids.length === 0) {
      toast.error("Select a customer and at least one pallet");
      return;
    }
    try {
      const { data } = await api.post("/shipments", form);
      toast.success(`Shipment ${data.shipment_number} created`);
      setShowForm(false);
      setForm({ customer_id: "", ship_date: new Date().toISOString().slice(0, 10), truck_number: "", notes: "", pallet_ids: [] });
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const markShipped = async (id) => {
    try {
      await api.patch(`/shipments/${id}`, { status: "shipped" });
      toast.success("Shipment marked as shipped");
      fetchAll();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const canManage = user?.role === "admin" || user?.role === "shipping";

  return (
    <div data-testid="shipments-page">
      <PageHeader
        eyebrow="Loading dock"
        title="Shipments."
        action={
          canManage && (
            <button onClick={() => setShowForm((v) => !v)} className="mj-btn mj-btn-primary" data-testid="new-shipment-btn">
              {showForm ? <><X size={14} /> Cancel</> : <><Plus size={14} /> New Shipment</>}
            </button>
          )
        }
      />

      <div className="p-6 md:p-10 space-y-6">
        {showForm && (
          <form onSubmit={submit} className="mj-card space-y-5" data-testid="shipment-form">
            <h3 className="font-display text-xl font-bold">Create Shipment</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="data-label block mb-2">Customer</label>
                <select required className="mj-input" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value, pallet_ids: [] })} data-testid="shipment-customer-select">
                  <option value="">Select customer…</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="data-label block mb-2">Ship date</label>
                <input type="date" className="mj-input font-mono-id" required value={form.ship_date} onChange={(e) => setForm({ ...form, ship_date: e.target.value })} data-testid="shipment-date-input" />
              </div>
              <div>
                <label className="data-label block mb-2">Truck # (optional)</label>
                <input className="mj-input font-mono-id" value={form.truck_number} onChange={(e) => setForm({ ...form, truck_number: e.target.value })} placeholder="TRK-0042" data-testid="shipment-truck-input" />
              </div>
            </div>

            <div>
              <label className="data-label block mb-2">Available pallets for this customer ({pallets.length})</label>
              {pallets.length === 0 ? (
                <div className="text-sm text-[var(--mj-ink-muted)] py-4 border border-dashed border-[var(--mj-border)]" style={{ textAlign: "center" }}>
                  {form.customer_id ? "No in-stock pallets assigned to this customer." : "Select a customer to see available pallets."}
                </div>
              ) : (
                <div className="border border-[var(--mj-border)] max-h-72 overflow-y-auto">
                  {pallets.map((p) => {
                    const checked = form.pallet_ids.includes(p.id);
                    return (
                      <label key={p.id} className={`flex items-center gap-3 p-3 border-b border-[var(--mj-border)] cursor-pointer ${checked ? "bg-[#fdf5d3]" : ""}`} data-testid={`shipment-pallet-row-${p.id}`}>
                        <input type="checkbox" checked={checked} onChange={() => togglePallet(p.id)} className="w-5 h-5" />
                        <span className="font-mono-id font-bold flex-1">{p.pallet_number}</span>
                        <span className="text-sm">{p.product_name}</span>
                        <span className="font-mono-id text-xs">{p.quantity} {p.units_label}</span>
                        <span className="font-mono-id text-xs">Exp {p.expiry_date}</span>
                        <StatusPill status={p.status} />
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <label className="data-label block mb-2">Notes</label>
              <textarea className="mj-input" style={{ minHeight: 60, padding: "10px 12px" }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="shipment-notes-input" />
            </div>
            <button type="submit" className="mj-btn mj-btn-primary" data-testid="shipment-submit-btn">Create Shipment</button>
          </form>
        )}

        <div className="mj-card">
          <table className="mj-table" data-testid="shipments-table">
            <thead>
              <tr>
                <th>Shipment #</th>
                <th>Customer</th>
                <th>Ship date</th>
                <th>Truck #</th>
                <th>Pallets</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {shipments.length === 0 && (
                <tr><td colSpan={7} className="text-center text-zinc-500 py-8">No shipments yet.</td></tr>
              )}
              {shipments.map((s) => (
                <tr key={s.id} data-testid={`shipment-row-${s.id}`}>
                  <td className="font-mono-id font-semibold">{s.shipment_number}</td>
                  <td>{s.customer_name}</td>
                  <td className="font-mono-id">{s.ship_date}</td>
                  <td className="font-mono-id">{s.truck_number || "—"}</td>
                  <td className="font-mono-id">{s.pallet_count}</td>
                  <td><StatusPill status={s.status} /></td>
                  <td className="text-right">
                    {canManage && s.status === "scheduled" && (
                      <button onClick={() => markShipped(s.id)} className="mj-btn mj-btn-primary" style={{ minHeight: 36, padding: "0 12px" }} data-testid={`mark-shipped-${s.id}`}>
                        <Check size={14} /> Ship
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
